import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { aiProjects, type AIProject } from '../../../mocks/aiProjects';
import { createAIProjectUrl } from '../../../hooks/useNavigation';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

const AIDramasGallery = () => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTool, setActiveTool] = useState('All');
  const [activeStyle, setActiveStyle] = useState('All');
  const [activeQuality, setActiveQuality] = useState('All');
  const [sortBy, setSortBy] = useState('trending');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    videoUrl: '',
    category: '',
    visualStyle: '',
    description: '',
    aiTools: '',
    thumbnailUrl: '',
    duration: '',
    quality: '',
    episodes: '',
    language: '',
    targetAudience: '',
    behindScenes: '',
    soundtrack: '',
    tags: '',
    socialLinks: '',
    agreeToGuidelines: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const categories = ['All', 'Drama', 'Sci-Fi', 'Comedy', 'Thriller', 'Horror', 'Romance', 'Action', 'Documentary', 'Experimental'];
  const tools = ['All', 'Runway', 'Sora', 'Pika Labs', 'Kling', 'Luma', 'Stable Video', 'Midjourney', 'DALL-E'];
  const styles = ['All', 'Cinematic', 'Anime', 'Realistic', 'Stylized', 'Abstract', 'Noir', 'Vintage'];
  const qualities = ['All', '4K', 'HD', 'SD'];

  const filteredProjects = useMemo(() => {
    let filtered = [...aiProjects];

    if (activeCategory !== 'All') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    if (activeTool !== 'All') {
      filtered = filtered.filter(p => p.tools.some(t => t.toLowerCase().includes(activeTool.toLowerCase())));
    }
    if (activeStyle !== 'All') {
      filtered = filtered.filter(p => p.style === activeStyle);
    }
    if (activeQuality !== 'All') {
      filtered = filtered.filter(p => p.quality === activeQuality);
    }

    switch (sortBy) {
      case 'trending':
        filtered.sort((a, b) => parseFloat(b.views.replace(/[^\d.]/g, '')) - parseFloat(a.views.replace(/[^\d.]/g, '')));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.datePublished).getTime() - new Date(a.datePublished).getTime());
        break;
      case 'mostLiked':
        filtered.sort((a, b) => parseFloat(b.likes.replace(/[^\d.]/g, '')) - parseFloat(a.likes.replace(/[^\d.]/g, '')));
        break;
      case 'duration':
        filtered.sort((a, b) => {
          const aDur = a.duration.split(':').reduce((acc, t) => acc * 60 + parseInt(t), 0);
          const bDur = b.duration.split(':').reduce((acc, t) => acc * 60 + parseInt(t), 0);
          return bDur - aDur;
        });
        break;
    }

    return filtered;
  }, [activeCategory, activeTool, activeStyle, activeQuality, sortBy]);

  const handlePlayVideo = (projectId: number) => {
    setPlayingVideo(playingVideo === projectId ? null : projectId);
  };

  const clearAllFilters = () => {
    setActiveCategory('All');
    setActiveTool('All');
    setActiveStyle('All');
    setActiveQuality('All');
    setSortBy('trending');
  };

  const activeFiltersCount = [activeCategory, activeTool, activeStyle, activeQuality].filter(f => f !== 'All').length;

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) errors.title = 'Project title is required';
    if (!formData.videoUrl.trim()) errors.videoUrl = 'Video URL is required';
    else if (!formData.videoUrl.match(/^https?:\/\/.+/)) errors.videoUrl = 'Please enter a valid URL';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.visualStyle) errors.visualStyle = 'Visual style is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    else if (formData.description.length < 50) errors.description = 'Description must be at least 50 characters';
    if (!formData.aiTools.trim()) errors.aiTools = 'AI tools used is required';
    if (!formData.duration.trim()) errors.duration = 'Duration is required';
    if (!formData.quality) errors.quality = 'Quality is required';
    if (!formData.language.trim()) errors.language = 'Language is required';
    if (!formData.agreeToGuidelines) errors.agreeToGuidelines = 'You must agree to the submission guidelines';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setToast({ type: 'error', message: 'Please log in to submit a project' });
      return;
    }

    if (!validateForm()) {
      setToast({ type: 'error', message: 'Please fix the errors in the form' });
      return;
    }

    setSubmitting(true);

    try {
      // Parse tags
      const tagsArray = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Parse AI tools
      const toolsArray = formData.aiTools
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Prepare metadata for AI drama specific fields
      const metadata = {
        type: 'ai_drama_submission',
        videoUrl: formData.videoUrl,
        visualStyle: formData.visualStyle,
        aiTools: toolsArray,
        thumbnailUrl: formData.thumbnailUrl || null,
        duration: formData.duration,
        quality: formData.quality,
        episodes: formData.episodes || null,
        language: formData.language,
        targetAudience: formData.targetAudience || null,
        behindScenes: formData.behindScenes || null,
        soundtrack: formData.soundtrack || null,
        socialLinks: formData.socialLinks || null,
      };

      // Insert into posts table with pending status
      const { data, error } = await supabase
        .from('posts')
        .insert({
          title: formData.title,
          content: formData.description,
          content_html: `<p>${formData.description}</p>`,
          author_id: user.id,
          category_id: null, // Can be mapped to a specific category if needed
          status: 'pending',
          tags: tagsArray,
          metadata,
          upvotes: 0,
          downvotes: 0,
          comment_count: 0,
          is_pinned: false,
          is_locked: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Show success message
      setToast({ type: 'success', message: 'Project submitted successfully! It will be reviewed by our team.' });
      
      // Reset form
      setFormData({
        title: '',
        videoUrl: '',
        category: '',
        visualStyle: '',
        description: '',
        aiTools: '',
        thumbnailUrl: '',
        duration: '',
        quality: '',
        episodes: '',
        language: '',
        targetAudience: '',
        behindScenes: '',
        soundtrack: '',
        tags: '',
        socialLinks: '',
        agreeToGuidelines: false,
      });
      setFormErrors({});

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowSubmitModal(false);
      }, 2000);

    } catch (error: any) {
      console.error('Error submitting project:', error);
      setToast({ type: 'error', message: error.message || 'Failed to submit project. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-hide toast after 5 seconds
  useState(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  });

  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
      {/* Modern Header with Gradient Banner */}
      <div className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 px-8 py-12">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start space-x-5">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30">
              <i className="ri-movie-2-fill text-white text-4xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-3xl font-bold text-white">AI Dramas Gallery</h2>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-bold rounded-full border border-white/30">
                  {aiProjects.length} Projects
                </span>
              </div>
              <p className="text-white/90 text-lg max-w-2xl">
                Explore cutting-edge AI-generated dramas from talented creators around the world
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg ${
                showFilters || activeFiltersCount > 0
                  ? 'bg-white text-emerald-600 border-2 border-white'
                  : 'bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white/30'
              }`}
            >
              <i className="ri-filter-3-fill text-lg"></i>
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="w-6 h-6 flex items-center justify-center bg-emerald-600 text-white text-xs font-bold rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowSubmitModal(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-white text-emerald-600 font-bold rounded-xl hover:bg-white/90 transition-all shadow-xl cursor-pointer whitespace-nowrap border-2 border-white"
            >
              <i className="ri-add-circle-fill text-xl"></i>
              <span>Submit Project</span>
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="relative mt-8 p-6 bg-white/95 backdrop-blur-sm rounded-2xl border-2 border-white shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <i className="ri-folder-3-fill text-emerald-600"></i>
                  Category
                </label>
                <select
                  value={activeCategory}
                  onChange={(e) => setActiveCategory(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Tool Filter */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <i className="ri-tools-fill text-emerald-600"></i>
                  AI Tool
                </label>
                <select
                  value={activeTool}
                  onChange={(e) => setActiveTool(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {tools.map(tool => (
                    <option key={tool} value={tool}>{tool}</option>
                  ))}
                </select>
              </div>

              {/* Style Filter */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <i className="ri-palette-fill text-emerald-600"></i>
                  Visual Style
                </label>
                <select
                  value={activeStyle}
                  onChange={(e) => setActiveStyle(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {styles.map(style => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
              </div>

              {/* Quality Filter */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <i className="ri-hd-fill text-emerald-600"></i>
                  Quality
                </label>
                <select
                  value={activeQuality}
                  onChange={(e) => setActiveQuality(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {qualities.map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Filter Tags */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t-2 border-slate-100">
              <span className="text-sm font-semibold text-slate-600">Quick filters:</span>
              <button
                onClick={() => { setActiveCategory('All'); setSortBy('trending'); }}
                className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-sm font-semibold rounded-full hover:from-orange-600 hover:to-rose-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                🔥 Trending
              </button>
              <button
                onClick={() => { setActiveCategory('All'); setSortBy('newest'); }}
                className="px-3 py-1.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-semibold rounded-full hover:from-violet-600 hover:to-purple-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                ✨ New Releases
              </button>
              <button
                onClick={() => setActiveQuality('4K')}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold rounded-full hover:from-blue-600 hover:to-cyan-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                📺 4K Only
              </button>
              <button
                onClick={() => setActiveTool('Sora')}
                className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                🎬 Made with Sora
              </button>
              <button
                onClick={() => setActiveStyle('Anime')}
                className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold rounded-full hover:from-pink-600 hover:to-rose-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                🎨 Anime Style
              </button>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-1.5 bg-slate-200 text-slate-700 text-sm font-semibold rounded-full hover:bg-slate-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line mr-1"></i>
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="px-8 py-5 bg-slate-50 border-b-2 border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="text-slate-600 text-sm font-medium">
            Showing <span className="text-emerald-600 font-bold text-base">{filteredProjects.length}</span> of {aiProjects.length} projects
          </span>
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2">
              {activeCategory !== 'All' && (
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full flex items-center gap-1.5 border border-emerald-200">
                  {activeCategory}
                  <button onClick={() => setActiveCategory('All')} className="hover:text-emerald-900 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {activeTool !== 'All' && (
                <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-bold rounded-full flex items-center gap-1.5 border border-cyan-200">
                  {activeTool}
                  <button onClick={() => setActiveTool('All')} className="hover:text-cyan-900 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {activeStyle !== 'All' && (
                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full flex items-center gap-1.5 border border-purple-200">
                  {activeStyle}
                  <button onClick={() => setActiveStyle('All')} className="hover:text-purple-900 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {activeQuality !== 'All' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full flex items-center gap-1.5 border border-blue-200">
                  {activeQuality}
                  <button onClick={() => setActiveQuality('All')} className="hover:text-blue-900 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-slate-700 text-sm font-semibold focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-300 transition-colors"
          >
            <option value="trending">🔥 Trending</option>
            <option value="newest">✨ Newest</option>
            <option value="mostLiked">❤️ Most Liked</option>
            <option value="duration">⏱️ Longest</option>
          </select>
          <div className="flex items-center bg-white rounded-xl p-1 border-2 border-slate-200">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-lg transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <i className="ri-grid-fill text-lg"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 rounded-lg transition-all cursor-pointer ${viewMode === 'list' ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <i className="ri-list-unordered text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="p-8 bg-gradient-to-br from-slate-50 to-white">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProjects.map((project) => (
              <Link
                to={createAIProjectUrl(project.slug)}
                key={project.id}
                className={`group relative bg-white rounded-2xl overflow-hidden border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                  project.featured ? 'border-emerald-400 shadow-lg shadow-emerald-100' : 'border-slate-200 hover:border-emerald-300'
                }`}
              >
                {/* Video/Thumbnail */}
                <div className="relative aspect-video overflow-hidden bg-slate-100">
                  {playingVideo === project.id ? (
                    <iframe
                      src={`${project.videoUrl}?autoplay=1`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <>
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
                      
                      <button
                        onClick={(e) => { e.preventDefault(); handlePlayVideo(project.id); }}
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                      >
                        <div className="w-16 h-16 flex items-center justify-center bg-emerald-500 rounded-full border-4 border-white hover:bg-emerald-600 hover:scale-110 transition-all shadow-2xl">
                          <i className="ri-play-fill text-white text-3xl ml-1"></i>
                        </div>
                      </button>

                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        {project.featured && (
                          <span className="px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg text-white text-xs font-bold flex items-center gap-1 shadow-lg border-2 border-white">
                            <i className="ri-star-fill"></i>
                            Featured
                          </span>
                        )}
                        <span className={`px-2.5 py-1 rounded-lg text-white text-xs font-bold shadow-lg border-2 border-white ${
                          project.quality === '4K' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 
                          project.quality === 'HD' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 
                          'bg-gradient-to-r from-slate-500 to-slate-600'
                        }`}>
                          {project.quality}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/80 backdrop-blur-sm rounded-lg text-white text-xs font-bold border border-white/20">
                        {project.duration}
                      </div>

                      <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-lg text-slate-700 text-xs font-bold border border-slate-200">
                        {project.category}
                      </div>

                      {project.awards.length > 0 && (
                        <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 backdrop-blur-sm rounded-lg text-white text-xs font-bold flex items-center gap-1 shadow-lg border-2 border-white">
                          <i className="ri-trophy-fill"></i>
                          {project.awards.length}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-bold text-slate-800 text-lg mb-2 group-hover:text-emerald-600 transition-colors line-clamp-1">
                    {project.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2 leading-relaxed">{project.description}</p>

                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                    <div className="flex items-center space-x-2">
                      <img
                        src={project.creator.avatar}
                        alt={project.creator.name}
                        className="w-8 h-8 rounded-full object-cover border-2 border-emerald-200"
                      />
                      <div className="flex items-center space-x-1">
                        <span className="text-slate-700 text-sm font-semibold">{project.creator.name}</span>
                        {project.creator.verified && (
                          <i className="ri-verified-badge-fill text-emerald-500 text-base"></i>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-slate-500 text-sm mb-3">
                    <span className="flex items-center gap-1.5 font-medium">
                      <i className="ri-eye-line text-base"></i>
                      {project.views}
                    </span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <i className="ri-heart-line text-base"></i>
                      {project.likes}
                    </span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <i className="ri-chat-3-line text-base"></i>
                      {project.comments}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {project.tools.slice(0, 2).map((tool, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-md border border-slate-200"
                      >
                        {tool}
                      </span>
                    ))}
                    {project.tools.length > 2 && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-md border border-emerald-200">
                        +{project.tools.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-5">
            {filteredProjects.map((project) => (
              <Link
                to={createAIProjectUrl(project.slug)}
                key={project.id}
                className={`group flex flex-col md:flex-row gap-5 bg-white rounded-2xl overflow-hidden border-2 transition-all duration-300 p-5 hover:shadow-2xl ${
                  project.featured ? 'border-emerald-400 shadow-lg shadow-emerald-100' : 'border-slate-200 hover:border-emerald-300'
                }`}
              >
                {/* Thumbnail */}
                <div className="relative w-full md:w-80 aspect-video md:aspect-auto md:h-44 flex-shrink-0 rounded-xl overflow-hidden bg-slate-100">
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <button
                    onClick={(e) => { e.preventDefault(); handlePlayVideo(project.id); }}
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  >
                    <div className="w-14 h-14 flex items-center justify-center bg-emerald-500 rounded-full border-4 border-white hover:bg-emerald-600 hover:scale-110 transition-all shadow-2xl">
                      <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
                    </div>
                  </button>
                  <span className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/80 backdrop-blur-sm rounded-lg text-white text-xs font-bold">
                    {project.duration}
                  </span>
                  {project.featured && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg text-white text-xs font-bold flex items-center gap-1 shadow-lg border-2 border-white">
                      <i className="ri-star-fill"></i>
                      Featured
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-slate-800 text-xl group-hover:text-emerald-600 transition-colors">
                          {project.title}
                        </h3>
                        <span className={`px-2.5 py-1 rounded-lg text-white text-xs font-bold ${
                          project.quality === '4K' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 
                          project.quality === 'HD' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 
                          'bg-gradient-to-r from-slate-500 to-slate-600'
                        }`}>
                          {project.quality}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2 leading-relaxed">{project.description}</p>
                    </div>
                    {project.awards.length > 0 && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl text-xs font-bold whitespace-nowrap shadow-lg">
                        <i className="ri-trophy-fill text-base"></i>
                        {project.awards[0]}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
                    <div className="flex items-center gap-2">
                      <img
                        src={project.creator.avatar}
                        alt={project.creator.name}
                        className="w-7 h-7 rounded-full object-cover border-2 border-emerald-200"
                      />
                      <span className="text-slate-700 font-semibold">{project.creator.name}</span>
                      {project.creator.verified && (
                        <i className="ri-verified-badge-fill text-emerald-500"></i>
                      )}
                    </div>
                    <span className="text-slate-300">•</span>
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg border border-slate-200">{project.category}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-600 flex items-center gap-1.5 font-medium">
                      <i className="ri-eye-line"></i> {project.views}
                    </span>
                    <span className="text-slate-600 flex items-center gap-1.5 font-medium">
                      <i className="ri-heart-line"></i> {project.likes}
                    </span>
                    <span className="text-slate-600 flex items-center gap-1.5 font-medium">
                      <i className="ri-chat-3-line"></i> {project.comments}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {project.tools.map((tool, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-md border border-slate-200"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl border-2 border-emerald-200">
              <i className="ri-film-line text-5xl text-emerald-500"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3">No projects found</h3>
            <p className="text-slate-600 mb-8 text-lg">Try adjusting your filters to discover more amazing AI drama projects.</p>
            <button
              onClick={clearAllFilters}
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-xl"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* View More Link */}
      <div className="px-8 py-6 bg-slate-50 border-t-2 border-slate-200 text-center">
        <Link
          to="/forum?tab=ai-dramas"
          className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all cursor-pointer shadow-xl hover:shadow-2xl hover:-translate-y-1"
        >
          <span>Explore AI Dramas Forum</span>
          <i className="ri-arrow-right-line text-xl"></i>
        </Link>
      </div>

      {/* Enhanced Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl border-2 border-slate-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl shadow-lg">
                    <i className="ri-upload-cloud-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800">Submit Your AI Drama</h3>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Row 1: Title */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Project Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder="Enter your project title"
                    className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
                      formErrors.title ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                    }`}
                  />
                  {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
                </div>

                {/* Row 2: Video URL */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Video URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => handleFormChange('videoUrl', e.target.value)}
                    placeholder="YouTube, Vimeo, or direct video link"
                    className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
                      formErrors.videoUrl ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                    }`}
                  />
                  {formErrors.videoUrl && <p className="text-red-500 text-xs mt-1">{formErrors.videoUrl}</p>}
                </div>

                {/* Row 3: Category, Visual Style, Quality */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 focus:outline-none focus:bg-white cursor-pointer transition-colors ${
                        formErrors.category ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                      }`}
                    >
                      <option value="">Select category</option>
                      {categories.filter(c => c !== 'All').map(cat => (
                        <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                      ))}
                    </select>
                    {formErrors.category && <p className="text-red-500 text-xs mt-1">{formErrors.category}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Visual Style <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.visualStyle}
                      onChange={(e) => handleFormChange('visualStyle', e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 focus:outline-none focus:bg-white cursor-pointer transition-colors ${
                        formErrors.visualStyle ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                      }`}
                    >
                      <option value="">Select style</option>
                      {styles.filter(s => s !== 'All').map(style => (
                        <option key={style} value={style.toLowerCase()}>{style}</option>
                      ))}
                    </select>
                    {formErrors.visualStyle && <p className="text-red-500 text-xs mt-1">{formErrors.visualStyle}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Quality <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.quality}
                      onChange={(e) => handleFormChange('quality', e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 focus:outline-none focus:bg-white cursor-pointer transition-colors ${
                        formErrors.quality ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                      }`}
                    >
                      <option value="">Select quality</option>
                      {qualities.filter(q => q !== 'All').map(quality => (
                        <option key={quality} value={quality}>{quality}</option>
                      ))}
                    </select>
                    {formErrors.quality && <p className="text-red-500 text-xs mt-1">{formErrors.quality}</p>}
                  </div>
                </div>

                {/* Row 4: Description */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Description <span className="text-red-500">*</span>
                    <span className="text-slate-500 font-normal ml-2">({formData.description.length}/500 characters, min 50)</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your AI drama project..."
                    className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white resize-none transition-colors ${
                      formErrors.description ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                    }`}
                  ></textarea>
                  {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                </div>

                {/* Row 5: AI Tools Used */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    AI Tools Used <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.aiTools}
                    onChange={(e) => handleFormChange('aiTools', e.target.value)}
                    placeholder="e.g., Runway Gen-4, Sora, ElevenLabs (comma separated)"
                    className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
                      formErrors.aiTools ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                    }`}
                  />
                  {formErrors.aiTools && <p className="text-red-500 text-xs mt-1">{formErrors.aiTools}</p>}
                </div>

                {/* Row 6: Duration, Episodes, Language */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Duration <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => handleFormChange('duration', e.target.value)}
                      placeholder="e.g., 5:30 or 1:20:00"
                      className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
                        formErrors.duration ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                      }`}
                    />
                    {formErrors.duration && <p className="text-red-500 text-xs mt-1">{formErrors.duration}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Number of Episodes</label>
                    <input
                      type="number"
                      value={formData.episodes}
                      onChange={(e) => handleFormChange('episodes', e.target.value)}
                      placeholder="e.g., 1, 5, 10"
                      min="1"
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Language <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.language}
                      onChange={(e) => handleFormChange('language', e.target.value)}
                      placeholder="e.g., English, Spanish"
                      className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
                        formErrors.language ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
                      }`}
                    />
                    {formErrors.language && <p className="text-red-500 text-xs mt-1">{formErrors.language}</p>}
                  </div>
                </div>

                {/* Row 7: Thumbnail URL */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Thumbnail URL</label>
                  <input
                    type="url"
                    value={formData.thumbnailUrl}
                    onChange={(e) => handleFormChange('thumbnailUrl', e.target.value)}
                    placeholder="Link to your project thumbnail"
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Row 8: Target Audience */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Target Audience</label>
                  <input
                    type="text"
                    value={formData.targetAudience}
                    onChange={(e) => handleFormChange('targetAudience', e.target.value)}
                    placeholder="e.g., Young adults, Sci-fi enthusiasts, General audience"
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Row 9: Behind the Scenes Notes */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Behind the Scenes Notes</label>
                  <textarea
                    value={formData.behindScenes}
                    onChange={(e) => handleFormChange('behindScenes', e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Share insights about your creative process, challenges, or interesting facts..."
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white resize-none transition-colors"
                  ></textarea>
                </div>

                {/* Row 10: Soundtrack Info */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Soundtrack Information</label>
                  <input
                    type="text"
                    value={formData.soundtrack}
                    onChange={(e) => handleFormChange('soundtrack', e.target.value)}
                    placeholder="Music credits, AI music tools used, or composer information"
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Row 11: Tags/Keywords */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Tags/Keywords</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleFormChange('tags', e.target.value)}
                    placeholder="e.g., cyberpunk, emotional, action-packed (comma separated)"
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Row 12: Social Links */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Creator Social Links</label>
                  <input
                    type="text"
                    value={formData.socialLinks}
                    onChange={(e) => handleFormChange('socialLinks', e.target.value)}
                    placeholder="Your website, Twitter, Instagram, etc. (comma separated)"
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Row 13: Agreement Checkbox */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agreeToGuidelines}
                      onChange={(e) => handleFormChange('agreeToGuidelines', e.target.checked)}
                      className={`w-5 h-5 rounded border-2 text-emerald-600 focus:ring-emerald-500 cursor-pointer mt-0.5 ${
                        formErrors.agreeToGuidelines ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    <span className="text-sm text-slate-700">
                      I agree to the submission guidelines and confirm that this is original content created primarily using AI tools. <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {formErrors.agreeToGuidelines && <p className="text-red-500 text-xs mt-1 ml-8">{formErrors.agreeToGuidelines}</p>}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSubmitModal(false)}
                    disabled={submitting}
                    className="flex-1 px-6 py-4 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Project'
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-8 p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200">
                <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center space-x-2">
                  <i className="ri-information-line text-emerald-600 text-lg"></i>
                  <span>Submission Guidelines</span>
                </h4>
                <ul className="text-sm text-slate-600 space-y-2 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>Projects must be primarily created using AI tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>Video should be at least 1 minute long</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>Original content only - no copyrighted material</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>Include credits for all AI tools used</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>High-quality 4K submissions are prioritized for featuring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-emerald-500 mt-0.5"></i>
                    <span>All submissions will be reviewed by our team before publication</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-8 right-8 z-50 animate-slide-up">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border-2 ${
            toast.type === 'success' 
              ? 'bg-emerald-50 border-emerald-500 text-emerald-800' 
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <i className={`text-2xl ${
              toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'
            }`}></i>
            <p className="font-semibold">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-70 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDramasGallery;
